import { createSelector } from '@reduxjs/toolkit';
import { selectGeoFromBootstrap } from 'store/slice/bootstrap';
import { Geo } from 'core/type/BootstrapConfig.types';
import { getSearchPageFromCityIdAndKeyword, getSearchPageUrlFromGlobalKeyword } from 'core/utils/activityUrlGenerator';
import { routeTo, selectIsGlobalKeywordSearchPage, selectPageTypeId } from 'store/slice/navigation';
import { selectScreenSize } from 'store/slice/screen';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import {
    ActivitiesInCitySearchResultClickEventActivitiesFieldsV1,
    ActivitiesGlobalKeywordSearchSeeAllResultClickEventActivitiesFieldsV1,
} from '@agoda/analytics-messages-definition-activities/events.split';
import {
    ActivitiesInCitySearchResultClickEventActivitiesManifestV1,
    ActivitiesGlobalKeywordSearchSeeAllResultClickEventActivitiesManifestV1,
} from '@agoda/analytics-messages-definition-activities/manifest/events';
import { isCityPage, isSearchPage } from 'core/constant/analytics/AnalyticsPageType';

export enum SearchEntryPointType {
    InCity,
    Global,
}

interface StateProps {
    geo?: Geo;
    isDesktop: boolean;
    canShowInCitySearch: boolean;
}

interface DispatchProps {
    onClickHandler: (searchType: SearchEntryPointType, keyword: string, geo?: Geo) => void;
}

interface OwnProps {
    searchType: SearchEntryPointType;
    inputText: string;
    cms: string;
    showPrefixIcon?: boolean;
    showSuffixIcon?: boolean;
}

export type SearchEntryPointOverlayProps = OwnProps & StateProps & DispatchProps;

export const mapStateToProps = createSelector(
    selectGeoFromBootstrap,
    selectScreenSize,
    selectPageTypeId,
    selectIsGlobalKeywordSearchPage,
    (geo, screenSize, pageTypeId, isGlobalKeywordSearchPage): StateProps => {
        return {
            geo,
            isDesktop: screenSize.desktop,
            canShowInCitySearch: isCityPage(pageTypeId) || (isSearchPage(pageTypeId) && !isGlobalKeywordSearchPage),
        };
    },
);

export const getTestIds = (searchType: SearchEntryPointType): { containerTestId: string; itemTestId: string } => {
    switch (searchType) {
        case SearchEntryPointType.InCity:
            return {
                containerTestId: 'in-city-content-container',
                itemTestId: 'activities-in-city-search-see-all-result',
            };
        case SearchEntryPointType.Global:
            return {
                containerTestId: 'global-search-content-container',
                itemTestId: 'activities-global-keyword-search-see-all-result',
            };
        default:
            return { containerTestId: '', itemTestId: '' };
    }
};

export const getSearchPageUrlByEntryPoint = (searchType: SearchEntryPointType, keyword: string, geo?: Geo): string | undefined => {
    switch (searchType) {
        case SearchEntryPointType.InCity:
            if (geo) {
                return getSearchPageFromCityIdAndKeyword(geo.cityId, keyword);
            }
            break;
        case SearchEntryPointType.Global:
            return getSearchPageUrlFromGlobalKeyword(keyword);
        default:
            return undefined;
    }

    return undefined;
};

const trackEvent = (searchType: SearchEntryPointType, inputText: string) => {
    switch (searchType) {
        case SearchEntryPointType.InCity:
            trackElementEvent<ActivitiesInCitySearchResultClickEventActivitiesFieldsV1>(ActivitiesInCitySearchResultClickEventActivitiesManifestV1.ids, {
                search_phrase: inputText,
            });
            break;
        case SearchEntryPointType.Global:
            trackElementEvent<ActivitiesGlobalKeywordSearchSeeAllResultClickEventActivitiesFieldsV1>(ActivitiesGlobalKeywordSearchSeeAllResultClickEventActivitiesManifestV1.ids, {
                search_phrase: inputText,
            });
            break;
        default:
            break;
    }
};

export const mapDispatchToProps = (dispatch: Function): DispatchProps => ({
    onClickHandler: (searchType: SearchEntryPointType, keyword: string, geo?: Geo) => {
        const url = getSearchPageUrlByEntryPoint(searchType, keyword, geo);
        trackEvent(searchType, keyword);
        if (url) {
            dispatch(routeTo(url));
        }
    },
});
